/**
 * Turn an ordered `ApiMessagePart[]` into the ordered RENDER ITEMS one Marvin message
 * draws, top-to-bottom. The parts already carry true event order in their `seq`, so this
 * is a single forward walk — no content-offset slicing, no card-array merging. It replaces
 * the old `marvinInterleave` (which cut a single answer blob against reasoning offsets).
 *
 * Consecutive `activity` parts collapse into one activity timeline (helper-tagged runs
 * split into their own collapsible MiniMarv block); `text` parts become markdown chunks;
 * every card / artifact part becomes its own item at its position in the stream, so a
 * mid-turn ask_user / approval card renders exactly where it happened.
 */
import type { ArtifactMeta } from "../shared/interfaces";
import {
    parseApiMessagePart,
    type ApiMessagePart,
    type MarvinAction,
    type MarvinMemoryUpdate,
    type MarvinNavigationSuggestion,
    type MarvinQuestion,
} from "../marvin/messageParts";
import { artifactPresentation } from "../marvin/renderSemantics";
import {
    ActivityPhase,
    ProgressLine,
    activityPartsToTrace,
    buildActivityPhases,
    helperIdOf,
} from "../hooks/streamReducer";

/** The name a helper block carries when its parts name none (records from before the name was sent). */
export const DEFAULT_HELPER_NAME = "MiniMarv";

/** One line shown inside a helper block, in stream order: a progress thought, a search query
 * or another lookup (a find, an open). The block renders a search as `Searched: "<text>"`. */
export interface HelperDisplayLine {
    id: string;
    kind: "progress" | "search" | "lookup";
    text: string;
}

/** One render item of a Marvin message, in draw order. */
export type RenderItem =
    | { kind: "text"; id: string; content: string }
    | { kind: "activity"; id: string; phases: ActivityPhase[] }
    | {
          kind: "helper";
          id: string;
          /** `helperId ?? researchId`: what the block groups by. */
          helperId: string;
          /** "MiniMarv"; the name the parts carry, else the default. */
          name: string;
          lines: HelperDisplayLine[];
          /** Every lookup the helper made, searches included. */
          lookupCount: number;
          searchCount: number;
          excerptCount: number | null;
      }
    | { kind: "question"; id: string; seq: number; question: MarvinQuestion }
    | { kind: "action"; id: string; seq: number; action: MarvinAction }
    | { kind: "memory"; id: string; seq: number; update: MarvinMemoryUpdate }
    | { kind: "navigation"; id: string; seq: number; suggestion: MarvinNavigationSuggestion }
    | { kind: "artifact-image"; id: string; artifact: ArtifactMeta }
    | { kind: "artifact-inline-html"; id: string; artifact: ArtifactMeta }
    | { kind: "artifact-panel"; id: string; artifact: ArtifactMeta };

/** An interactive HTML artifact the agent chose to render inline in the message flow. */
export function isInlineHtmlArtifact(a: ArtifactMeta): boolean {
    return artifactPresentation(a) === "inline-html";
}

/** Build one helper block's display model from its (already helper-grouped) lines. */
function buildHelperBlock(
    helperId: string,
    lines: ProgressLine[],
): Extract<RenderItem, { kind: "helper" }> {
    const display: HelperDisplayLine[] = [];
    let searchCount = 0;
    let lookupCount = 0;
    let excerptSum = 0;
    let excerptResults = 0;
    let allParsed = true;
    let name = "";

    for (const line of lines) {
        name = name || line.helperName || "";
        if (line.kind === "thinking") {
            if (line.text.trim()) display.push({ id: line.id, kind: "progress", text: line.text });
        } else if (line.kind === "tool") {
            lookupCount += 1;
            if (line.toolName === "search_documents") {
                searchCount += 1;
                display.push({ id: line.id, kind: "search", text: line.text });
            } else {
                display.push({ id: line.id, kind: "lookup", text: line.text });
            }
        } else {
            // kind="result": tally excerpts from search_documents results only. Another lookup's count
            // rides its own line (resultCount), so a result line of another tool is not a second lookup.
            if (line.toolName !== "search_documents") continue;
            excerptResults += 1;
            if (typeof line.excerptCount === "number") excerptSum += line.excerptCount;
            else allParsed = false;
        }
    }
    // N is shown only when EVERY result parsed cleanly, else a searches-only summary.
    const excerptCount = excerptResults > 0 && allParsed ? excerptSum : null;
    return {
        kind: "helper",
        id: `helper-${helperId}`,
        helperId,
        name: name || DEFAULT_HELPER_NAME,
        lines: display,
        lookupCount,
        searchCount,
        excerptCount,
    };
}

/**
 * Flush a contiguous run of activity lines into items, preserving order: main-agent lines
 * (no helper id) coalesce into one activity timeline; each helper delegation (grouped by
 * `helperId ?? researchId`, emitted at its first line) becomes its own helper block. A helper
 * delegation between two main runs splits them, so two activity items are never adjacent.
 */
function flushActivity(lines: ProgressLine[], items: RenderItem[]): void {
    if (lines.length === 0) return;
    const helperGroups = new Map<string, ProgressLine[]>();
    let mainRun: ProgressLine[] = [];

    const flushMain = () => {
        if (mainRun.length === 0) return;
        const phases = buildActivityPhases(mainRun);
        const last = items[items.length - 1];
        if (last && last.kind === "activity") {
            last.phases = [...last.phases, ...phases];
        } else {
            items.push({ kind: "activity", id: `act-${mainRun[0].id}`, phases });
        }
        mainRun = [];
    };

    for (const line of lines) {
        const helperId = helperIdOf(line);
        if (helperId) {
            flushMain();
            const existing = helperGroups.get(helperId);
            if (existing) {
                existing.push(line);
            } else {
                const group = [line];
                helperGroups.set(helperId, group);
                items.push(buildHelperBlock(helperId, group));
            }
        } else {
            mainRun.push(line);
        }
    }
    flushMain();
    // Re-materialize each helper block now that all its lines are gathered (the item
    // pushed above holds a live reference to the same array, so re-derive its summary).
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "helper") {
            const gathered = helperGroups.get(item.helperId);
            if (gathered) items[i] = buildHelperBlock(item.helperId, gathered);
        }
    }
}

/** Walk the ordered parts into ordered render items (see module docstring). */
export function buildRenderItems(parts: ApiMessagePart[]): RenderItem[] {
    const items: RenderItem[] = [];
    let activityBuffer: ProgressLine[] = [];

    const flush = () => {
        flushActivity(activityBuffer, items);
        activityBuffer = [];
    };

    for (const candidate of parts) {
        const part = parseApiMessagePart(candidate);
        if (!part) continue;
        switch (part.kind) {
            case "activity": {
                // activityPartsToTrace resolves the display text (tool lines prefer the rich
                // `label`) and keys the id on seq for stable React keys.
                const [line] = activityPartsToTrace([part]);
                activityBuffer.push(line);
                break;
            }
            case "text": {
                flush();
                const content = part.payload.text || "";
                if (content) items.push({ kind: "text", id: `text-${part.seq}`, content });
                break;
            }
            case "reference":
            case "context_stats":
                // Non-visual: consumed by the reference map / context meter selectors, not
                // drawn inline. Don't break an activity run around them.
                break;
            case "question":
                flush();
                items.push({
                    kind: "question",
                    id: `q-${part.seq}`,
                    seq: part.seq,
                    question: { ...part.payload, seq: part.seq },
                });
                break;
            case "action":
                flush();
                items.push({
                    kind: "action",
                    id: `a-${part.seq}`,
                    seq: part.seq,
                    action: { ...part.payload, seq: part.seq },
                });
                break;
            case "memory_notice":
                flush();
                items.push({
                    kind: "memory",
                    id: `m-${part.seq}`,
                    seq: part.seq,
                    update: { ...part.payload, seq: part.seq },
                });
                break;
            case "navigation":
                flush();
                items.push({
                    kind: "navigation",
                    id: `n-${part.seq}`,
                    seq: part.seq,
                    suggestion: { ...part.payload, seq: part.seq },
                });
                break;
            case "artifact": {
                flush();
                const artifact = part.payload;
                const id = `artifact-${artifact.id}`;
                const presentation = artifactPresentation(artifact);
                if (presentation === "inline-image") {
                    items.push({ kind: "artifact-image", id, artifact });
                } else if (presentation === "inline-html") {
                    items.push({ kind: "artifact-inline-html", id, artifact });
                } else {
                    items.push({ kind: "artifact-panel", id, artifact });
                }
                break;
            }
            default:
                break;
        }
    }
    flush();
    return items;
}
